import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, FileQuestion, AlertCircle, TrendingUp, CheckCircle, ArrowRight } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const hStyle: React.CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontWeight: 800,
  color: 'var(--bw-dark)',
  letterSpacing: '-0.025em',
  lineHeight: 1.15,
};

const Blueprint = () => {
  const problems = [
    {
      Icon: MapPin,
      title: 'No Technical Roadmap',
      description: "You have a vision but don't know where to start technically. Should you use PostgreSQL or MongoDB? EC2 or Lambda? You're making expensive decisions blind.",
    },
    {
      Icon: FileQuestion,
      title: 'Confusing Proposals',
      description: "You've got 3 proposals from dev shops and they all sound like gibberish. One says Kubernetes, another says serverless. Who's actually right?",
    },
    {
      Icon: AlertCircle,
      title: 'Already Building Wrong',
      description: "Your developer is 6 weeks in and you're worried they're going the wrong direction. No compliance plan. No scaling strategy. Just hoping it works out.",
    },
    {
      Icon: TrendingUp,
      title: 'Moving Too Fast',
      description: "You need to move quickly but can't afford expensive mistakes. One wrong architectural decision costs months of rework and tens of thousands of dollars.",
    },
  ];

  const deliverables = [
    { title: 'Complete System Architecture', desc: 'Diagrams showing how everything connects' },
    { title: 'Database Design', desc: 'Entity relationships and schema specifications' },
    { title: 'API Structure', desc: 'Endpoint design and authentication strategy' },
    { title: 'AWS Infrastructure Plan', desc: 'Exact services and cost estimates' },
    { title: 'Security & Compliance Roadmap', desc: 'SOC 2, HIPAA if applicable' },
    { title: '3-Phase Implementation Timeline', desc: 'MVP to production-ready' },
    { title: 'Cost Estimates by Phase', desc: "Know what you're actually spending" },
  ];

  const tiers = [
    {
      tier: 'Phase 1',
      name: 'Blueprint Only',
      price: 'From $3K',
      timeline: '2 Weeks',
      featured: false,
      features: [
        '90-minute discovery call',
        'Complete technical blueprint (15 pages)',
        'Architecture diagrams',
        'Database design',
        'Infrastructure recommendations',
        'Cost estimates by phase',
        '60-minute review call',
      ],
    },
    {
      tier: 'Phase 2',
      name: 'Blueprint + Advisory',
      price: 'From $5K',
      timeline: '4 Weeks',
      featured: true,
      features: [
        'Everything in Phase 1, PLUS:',
        '4 weekly advisory calls (60 min each)',
        'Review vendor proposals',
        'Architecture reviews during build',
        'Slack/email support',
        'Code review of critical components',
      ],
    },
    {
      tier: 'Phase 3',
      name: 'Blueprint + Build',
      price: 'Starting at $10K',
      timeline: '12–16 Weeks',
      featured: false,
      features: [
        'Everything in Phase 1, PLUS:',
        'Complete Phase 1 MVP development',
        'AWS infrastructure setup',
        'Database implementation',
        'API development',
        'CI/CD pipeline',
        'Testing and QA',
        'Production deployment',
      ],
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bw-white)' }}>
      <Helmet>
        <title>Technical Blueprint — Don't Build Blind | BrightWings</title>
        <meta name="description" content="Get a complete technical blueprint before you write a single line of code. Architecture, scope, and implementation roadmap delivered in 2 weeks. From $3K." />
        <link rel="canonical" href="https://brightwings.io/technical-blueprint" />
        <meta property="og:url" content="https://brightwings.io/technical-blueprint" />
        <meta property="og:title" content="Technical Blueprint — Don't Build Blind | BrightWings" />
        <meta property="og:description" content="Get a complete technical blueprint before you write a single line of code. Architecture, scope, and implementation roadmap delivered in 2 weeks. From $3K." />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Technical Blueprint — Don't Build Blind | BrightWings" />
        <meta name="twitter:description" content="Get a complete technical blueprint before you write a single line of code. Architecture, scope, and implementation roadmap delivered in 2 weeks. From $3K." />
      </Helmet>
      <Navigation />

      {/* Hero */}
      <section className="bw-hero">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bw-eyebrow-pill">For Founders</div>
            <h1 style={{ ...hStyle, fontSize: 'clamp(36px, 5.5vw, 72px)', marginBottom: '20px' }}>
              Don't Build <em style={{ fontStyle: 'normal', color: 'var(--bw-blue)' }}>Blind.</em>
            </h1>
            <p style={{ fontSize: 'clamp(16px, 2vw, 20px)', color: 'var(--bw-text-mid)', lineHeight: 1.7, maxWidth: '540px', margin: '0 auto 32px' }}>
              Get a complete technical blueprint before you build a single line of code. Architecture, scope, and implementation roadmap delivered in 2 weeks.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
                Start a conversation
              </Link>
              <a href="#process" className="bw-btn-secondary">How It Works</a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <div className="bw-stats-bar">
        <div className="max-w-4xl mx-auto w-full flex flex-wrap justify-center">
          {[
            { val: '2 wks', lbl: 'Delivery time' },
            { val: '15 pg', lbl: 'Blueprint depth' },
            { val: '3 phase', lbl: 'Implementation plan' },
          ].map((s, i) => (
            <div key={i} className="bw-stat-item">
              <div className="bw-stat-val">{s.val}</div>
              <div className="bw-stat-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Problem */}
      <section className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <span className="bw-eyebrow">The Problem</span>
            <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 42px)' }}>
              Most Technical Decisions Get Made<br />Before Anyone Has a Plan.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {problems.map((p, i) => (
              <div key={i} className="bw-card" style={{ borderLeft: '3px solid var(--bw-blue)' }}>
                <div className="bw-card-body">
                  <div className="flex items-center gap-3 mb-12">
                    <div className="bw-feature-icon" style={{ marginBottom: 0, flexShrink: 0 }}>
                      <p.Icon size={20} color="var(--bw-blue)" />
                    </div>
                    <h3 style={{ ...hStyle, fontSize: '17px', color: 'var(--bw-blue)' }}>{p.title}</h3>
                  </div>
                  <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '14px' }}>{p.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal problem */}
      <section className="bw-section-white" style={{ paddingTop: '32px', paddingBottom: '0' }}>
        <div className="max-w-6xl mx-auto">
          <div className="bw-callout" style={{ maxWidth: '680px' }}>
            The worst part isn't the technical confusion — it's the anxiety of knowing you're making expensive decisions you don't fully understand.
          </div>
        </div>
      </section>

      {/* Solution */}
      <section className="bw-section-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <span className="bw-eyebrow">The Solution</span>
              <h2 style={{ ...hStyle, fontSize: 'clamp(26px, 3.5vw, 42px)', marginBottom: '32px' }}>
                A Professional Technical Blueprint in 2 Weeks
              </h2>
              <ul className="space-y-4 mb-8">
                {deliverables.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 pb-4" style={{ borderBottom: '1px solid var(--bw-border)' }}>
                    <ArrowRight size={16} color="var(--bw-blue)" style={{ marginTop: '4px', flexShrink: 0 }} />
                    <div>
                      <strong style={{ display: 'block', color: 'var(--bw-dark)', fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: '15px' }}>{item.title}</strong>
                      <span style={{ fontSize: '13px', color: 'var(--bw-text-mid)' }}>{item.desc}</span>
                    </div>
                  </li>
                ))}
              </ul>
              <p style={{ color: 'var(--bw-text-mid)', lineHeight: 1.7, fontSize: '15px' }}>
                You own the blueprint. Build it yourself, hire us, or take it to any dev shop. Either way, you're making informed decisions instead of expensive guesses.
              </p>
            </div>

            <div className="bw-card" style={{ border: '2px solid var(--bw-blue)' }}>
              <div className="bw-card-header">
                <p style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--bw-blue)', fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                  What's inside
                </p>
              </div>
              <div className="bw-card-body">
                {[
                  { num: 1, title: 'Executive Summary', desc: 'Product vision, technical approach, timeline & budget' },
                  { num: 2, title: 'System Architecture', desc: 'Component diagrams, tech stack rationale, scaling path' },
                  { num: 3, title: 'Database Design', desc: 'ERD, schema specs, migration strategy' },
                  { num: 4, title: 'Infrastructure', desc: 'AWS services, networking, deployment, costs' },
                  { num: 5, title: 'Implementation Roadmap', desc: '3 phases: MVP → Beta → Production-ready' },
                ].map((layer, i) => (
                  <div
                    key={i}
                    className="p-4 mb-3 transition-all duration-200 hover:translate-x-1"
                    style={{
                      backgroundColor: 'var(--bw-surface)',
                      borderLeft: '3px solid var(--bw-blue)',
                      borderRadius: '0 8px 8px 0',
                    }}
                  >
                    <strong style={{ display: 'block', marginBottom: '4px', color: 'var(--bw-blue)', fontSize: '14px', fontFamily: 'var(--font-display)' }}>
                      {layer.num}. {layer.title}
                    </strong>
                    <p style={{ fontSize: '13px', color: 'var(--bw-text-mid)' }}>{layer.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Tiers */}
      <section id="process" className="bw-section-surface">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="bw-eyebrow">Service Options</span>
            <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 42px)' }}>Choose Your Level of Support</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => (
              <div
                key={i}
                className="bw-card flex flex-col"
                style={{
                  border: tier.featured ? '2px solid var(--bw-blue)' : undefined,
                  position: tier.featured ? 'relative' : undefined,
                  overflow: tier.featured ? 'visible' : undefined,
                }}
              >
                {tier.featured && (
                  <div
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-xs font-semibold tracking-wider uppercase whitespace-nowrap rounded-full"
                    style={{ backgroundColor: 'var(--bw-blue)', color: 'white', fontFamily: 'var(--font-display)' }}
                  >
                    Often where founders start
                  </div>
                )}
                <div className="bw-card-header">
                  <span className="bw-eyebrow" style={{ marginBottom: '8px' }}>{tier.tier}</span>
                  <h3 style={{ ...hStyle, fontSize: '20px', marginBottom: '4px' }}>{tier.name}</h3>
                  <p style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '22px', color: 'var(--bw-blue)', letterSpacing: '-0.02em', marginBottom: '4px' }}>{tier.price}</p>
                  <p style={{ fontSize: '13px', color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)' }}>Timeline: {tier.timeline}</p>
                </div>
                <div className="bw-card-body flex-grow flex flex-col">
                  <ul className="space-y-3 mb-8 flex-grow">
                    {tier.features.map((feature, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm pb-3" style={{ borderBottom: '1px solid var(--bw-border)' }}>
                        <CheckCircle size={14} color="var(--bw-blue)" style={{ marginTop: '3px', flexShrink: 0 }} />
                        <span style={{ color: 'var(--bw-text-mid)' }}>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    onClick={() => window.scrollTo(0, 0)}
                    className={tier.featured ? 'bw-btn-primary' : 'bw-btn-secondary'}
                    style={{ justifyContent: 'center', width: '100%' }}
                  >
                    Start a conversation
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stakes */}
      <section className="bw-section-surface">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <div style={{ borderLeft: '3px solid var(--bw-border)', paddingLeft: '24px' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-muted)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>Without a blueprint</p>
              <ul className="space-y-3">
                {[
                  'Six weeks in, you realize you built the wrong thing',
                  'You can\'t evaluate the proposals — they all sound the same',
                  'You\'re dependent on whoever is holding the code',
                ].map((item, i) => (
                  <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <span style={{ color: 'var(--bw-muted)', fontWeight: 700, flexShrink: 0 }}>×</span>
                    <span style={{ fontSize: '15px', color: 'var(--bw-text-mid)', lineHeight: 1.6 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ borderLeft: '3px solid var(--bw-blue)', paddingLeft: '24px' }}>
              <p style={{ fontSize: '12px', fontWeight: 600, color: 'var(--bw-blue)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '16px' }}>With a blueprint</p>
              <ul className="space-y-3">
                {[
                  'You walk into every developer conversation knowing exactly what to ask',
                  'You own the decisions — and can take the work anywhere',
                  'You make informed choices instead of expensive guesses',
                ].map((item, i) => (
                  <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, marginTop: '2px' }}>
                      <circle cx="10" cy="10" r="10" fill="var(--bw-blue)" />
                      <path d="M6 10L8.5 12.5L14 7" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span style={{ fontSize: '15px', color: 'var(--bw-text-mid)', lineHeight: 1.6 }}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bw-cta-section">
        <div className="max-w-4xl mx-auto">
          <h2 style={{ ...hStyle, fontSize: 'clamp(28px, 3.5vw, 44px)', marginBottom: '16px' }}>Build with direction. Ship with confidence.</h2>
          <p style={{ fontSize: '18px', color: 'var(--bw-text-mid)', marginBottom: '36px', lineHeight: 1.7 }}>
            Get technical clarity before committing to a build. Professional blueprint delivered in 2 weeks. Take it anywhere, or hire us to build it for you.
          </p>
          <Link to="/contact" onClick={() => window.scrollTo(0, 0)} className="bw-btn-primary">
            Start a conversation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blueprint;
